import { isEmpty, isNull, omit } from 'lodash';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { TNominationFilterKey } from '../nominationSidebar/NominationSidebarFilters';
import { ReadinessTrackingFilterKeys, TReadinessTrackingFilterKey } from './ReadinessTrackingSidebarFilters';
import { IReadinessTrackingPageState, IReadinessTrackingSidebarFilters } from './ReadinessTrackingSidebarFiltersContext';

export const transformReadinessTrackingSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TReadinessTrackingFilterKey, string> => {
    let result: Record<string, string> = {};

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case ReadinessTrackingFilterKeys.dateToSendEmail:
                result[key] = value;
                break;
            case ReadinessTrackingFilterKeys.programId:
                result[key] = value
                    .map((val: string) => {
                        const option = context.programOptions.find(option => option.value === val);
                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case ReadinessTrackingFilterKeys.userId:
                result[key] = value
                    .map((val: number) => {
                        const option = context.userOptions.find(option => option.value === val);
                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case ReadinessTrackingFilterKeys.status:
                result[key] = value
                    .map((val: string) => {
                        const option = context.nominationStatusOptions.find(option => option.value === val);
                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case ReadinessTrackingFilterKeys.emailOpened:
                result[key] = value ? 'Yes' : 'No';
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
                break;
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TReadinessTrackingFilterKey, string>;
    }

    return result;
};

export const convertReadinessTrackingPageStateToFilterState = (pageState: IReadinessTrackingPageState): IReadinessTrackingSidebarFilters => {
    const result: IReadinessTrackingSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert programs
    if (filterData.programId) {
        result.programId = Array.isArray(filterData.programId) ? filterData.programId : [filterData.programId];
    }

    // Convert users
    if (filterData.userId) {
        result.userId = Array.isArray(filterData.userId) ? filterData.userId : [filterData.userId];
    }

    // Convert status
    if (filterData.status) {
        result.status = Array.isArray(filterData.status) ? filterData.status : [filterData.status];
    }

    // Convert dates
    if (filterData.dateToSendEmail) {
        result.dateToSendEmail = filterData.dateToSendEmail;
    }

    // Convert email opened
    if (filterData.emailOpened !== undefined) {
        result.emailOpened = filterData.emailOpened;
    }

    return result;
};

export const transformReadinessTrackingFilterValuesFromDto = (
    filter: ISavedFilter['filter_values']
): Record<TReadinessTrackingFilterKey, unknown> => {
    const result: Record<TReadinessTrackingFilterKey, unknown> = {} as Record<TReadinessTrackingFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case ReadinessTrackingFilterKeys.programId:
            case ReadinessTrackingFilterKeys.userId:
            case ReadinessTrackingFilterKeys.dateToSendEmail:
            case ReadinessTrackingFilterKeys.emailOpened:
            case ReadinessTrackingFilterKeys.status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformReadinessTrackingFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TReadinessTrackingFilterKey, unknown> => {
    const result: Record<TReadinessTrackingFilterKey, unknown> = {} as Record<TReadinessTrackingFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case ReadinessTrackingFilterKeys.programId:
            case ReadinessTrackingFilterKeys.userId:
            case ReadinessTrackingFilterKeys.dateToSendEmail:
            case ReadinessTrackingFilterKeys.emailOpened:
            case ReadinessTrackingFilterKeys.status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case ReadinessTrackingFilterKeys.programId:
                const validProgramValues = context.programOptions.map(option => option.value);
                const invalidPrograms = Array.isArray(value)
                    ? value.filter(val => !validProgramValues.includes(val))
                    : !validProgramValues.includes(value);

                if ((Array.isArray(invalidPrograms) && invalidPrograms.length > 0) || (!Array.isArray(invalidPrograms) && invalidPrograms)) {
                    invalidFields.push(key);
                }
                break;

            case ReadinessTrackingFilterKeys.userId:
                const validUserValues = context.userOptions.map(option => option.value);
                const invalidUserValues = Array.isArray(value)
                    ? value.filter(val => !validUserValues.includes(val))
                    : !validUserValues.includes(value);

                if ((Array.isArray(invalidUserValues) && invalidUserValues.length > 0) || (!Array.isArray(invalidUserValues) && invalidUserValues)) {
                    invalidFields.push(key);
                }
                break;

            case ReadinessTrackingFilterKeys.status:
                const validStatusValues = context.nominationStatusOptions.map(option => option.value);
                const invalidStatusValues = Array.isArray(value)
                    ? value.filter(val => !validStatusValues.includes(val))
                    : !validStatusValues.includes(value);

                if (
                    (Array.isArray(invalidStatusValues) && invalidStatusValues.length > 0) ||
                    (!Array.isArray(invalidStatusValues) && invalidStatusValues)
                ) {
                    invalidFields.push(key);
                }

                break;

            // Add more key validations if needed

            default:
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: IReadinessTrackingSidebarFilters): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

export function getEmptyFilterProperties(obj: Record<TNominationFilterKey, string>): TNominationFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TNominationFilterKey[];
}
